import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Briefcase,
  Palette,
  Database,
  Upload,
  Settings,
  CheckCircle,
  Save,
  Eye,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  ExternalLink,
  Zap,
  Plus,
  ArrowLeft,
  Pencil,
  X,
  Image,
  Bookmark
} from 'lucide-react';
import { toast } from 'sonner';
import { AppProfileContext } from '@/contexts/AppProfileContext';

const API_URL = window.__BACKEND_URL;

/**
 * App Profile Page
 * 
 * PURPOSE: App Profiles exist exclusively to power the Self-Marketing Engine (SME).
 * An App Profile defines how a brand markets itself — NOT how the platform looks.
 * 
 * SECTIONS:
 * 1. Header - Avatar (from Symbol), Name, Description, SME badge
 * 2. Marketing Brand Kit (LEFT) - Format-scoped (SF/LF) configuration
 * 3. Live Marketing Preview (RIGHT) - Real-time preview of current format
 * 4. Storage & Platforms
 * 5. Save Button (atomic)
 */
export default function AppProfile() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { appProfiles, activeProfileId, selectProfile, refreshProfiles, loading } = useContext(AppProfileContext);
  const fileInputRef = useRef(null);
  
  // Platform connections
  const [platformStatus, setPlatformStatus] = useState(null);
  const [platformConfig, setPlatformConfig] = useState({});
  
  // Dropbox status
  const [dropboxStatus, setDropboxStatus] = useState(null);
  const [disconnectingDropbox, setDisconnectingDropbox] = useState(false);
  
  // Track unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Defaults state
  const [defaultsSaved, setDefaultsSaved] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  
  // Storage mode info
  const [storageMode, setStorageMode] = useState('temp');
  const [storageTTL, setStorageTTL] = useState(72);
  
  // Active format: 'sf' or 'lf'
  const [activeFormat, setActiveFormat] = useState('sf');
  
  // Get active profile
  const targetProfileId = profileId || activeProfileId;
  const activeProfile = useMemo(
    () => appProfiles.find((p) => p.id === targetProfileId) || appProfiles[0],
    [appProfiles, targetProfileId]
  );
  
  // Form state - Header fields
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  
  // Brand Kit state - Format-scoped (SF and LF)
  const [brandKit, setBrandKit] = useState({
    // Shared symbol (uploaded once)
    symbol_url: '',
    
    // Short Form config
    sf: {
      symbol_position: 'br', // tl, tr, bl, br
      captions_enabled: true,
      caption_font: 'Inter',
      caption_primary_color: '#FFFFFF',
      caption_secondary_color: '#3A7BFF',
      caption_position: 'bottom', // top, center, bottom
      progress_bar_enabled: true,
      progress_bar_color: '#FF1493',
      progress_bar_position: 'top' // top, bottom
    },
    
    // Long Form config
    lf: {
      symbol_position: 'br',
      captions_enabled: true,
      caption_font: 'Inter',
      caption_primary_color: '#FFFFFF',
      caption_secondary_color: '#3A7BFF',
      caption_position: 'bottom',
      progress_bar_enabled: false,
      progress_bar_color: '#FF1493',
      progress_bar_position: 'top' // top, bottom
    }
  });
  
  // Sync form when profile changes - fetch full profile data
  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!activeProfile?.id) return;
      
      try {
        // Fetch full profile with storage data
        const res = await fetch(`${API_URL}/api/admin/app-profiles/${activeProfile.id}`, {
          credentials: 'include'
        });
        
        if (res.ok) {
          const fullProfile = await res.json();
          
          setFormData({
            name: fullProfile.name || '',
            description: fullProfile.description || ''
          });
          
          // Load brand kit if exists
          if (fullProfile.brand_kit) {
            setBrandKit(prev => ({
              symbol_url: fullProfile.brand_kit.symbol_url || prev.symbol_url,
              sf: { ...prev.sf, ...(fullProfile.brand_kit.sf || {}) },
              lf: { ...prev.lf, ...(fullProfile.brand_kit.lf || {}) }
            }));
          }
          
          // Set defaults state
          setDefaultsSaved(fullProfile.has_defaults || false);
          
          // Set storage mode
          setStorageMode(fullProfile.storage_mode || 'temp');
          setStorageTTL(fullProfile.temp_ttl_hours || 72);
        }
      } catch (err) {
        console.error('Failed to fetch full profile:', err);
        // Fallback to context data
        if (activeProfile) {
          setFormData({
            name: activeProfile.name || '',
            description: activeProfile.description || ''
          });
          
          if (activeProfile.brand_kit) {
            setBrandKit(prev => ({
              symbol_url: activeProfile.brand_kit.symbol_url || prev.symbol_url,
              sf: { ...prev.sf, ...(activeProfile.brand_kit.sf || {}) },
              lf: { ...prev.lf, ...(activeProfile.brand_kit.lf || {}) }
            }));
          }
        }
      }
      
      setHasChanges(false);
    };
    
    fetchFullProfile();
    
    // Sync context if URL param differs
    if (profileId && profileId !== activeProfileId) {
      selectProfile(profileId);
    }
  }, [activeProfile?.id, profileId, activeProfileId, selectProfile]);
  
  // Fetch platform status and config
  const fetchPlatformStatus = useCallback(async () => {
    if (!activeProfile?.id) return;
    
    try {
      const [statusRes, configRes] = await Promise.all([
        fetch(`${API_URL}/api/social/status?owner_type=app_profile&owner_id=${activeProfile.id}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/config-status`, { credentials: 'include' })
      ]);
      if (statusRes.ok) {
        const data = await statusRes.json();
        setPlatformStatus(data);
      }
      if (configRes.ok) {
        setPlatformConfig(await configRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch platform status');
    }
  }, [activeProfile?.id]);
  
  // Fetch Dropbox status
  const fetchDropboxStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/storage/internal/status`, { credentials: 'include' });
      if (res.ok) {
        setDropboxStatus(await res.json());
      }
    } catch (err) {}
  }, []);
  
  // Disconnect internal admin Dropbox
  const handleDisconnectDropbox = async () => {
    if (!confirm('Are you sure you want to disconnect Dropbox? This will not delete any files.')) return;
    try {
      setDisconnectingDropbox(true);
      const res = await fetch(`${API_URL}/api/storage/internal/dropbox/disconnect`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Dropbox disconnected');
        await fetchDropboxStatus();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to disconnect Dropbox');
      }
    } catch (err) {
      toast.error('Failed to disconnect Dropbox');
    } finally {
      setDisconnectingDropbox(false);
    }
  };

  // Connect internal admin Dropbox (redirect to OAuth)
  const handleConnectDropbox = () => {
    window.location.href = `${API_URL}/api/storage/internal/dropbox/connect`;
  };

  useEffect(() => {
    fetchPlatformStatus();
    fetchDropboxStatus();
    
    // Handle OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'connected') {
      toast.success('Platform connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchPlatformStatus();
    } else if (params.get('dropbox') === 'connected') {
      toast.success('Dropbox connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchDropboxStatus();
    } else if (params.get('error')) {
      toast.error(`Connection failed: ${params.get('error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchPlatformStatus, fetchDropboxStatus]);
  
  // Handle header field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };
  
  // Handle brand kit changes (format-scoped)
  const handleBrandKitChange = (field, value) => {
    if (field === 'symbol_url') {
      setBrandKit(prev => ({ ...prev, symbol_url: value }));
    } else {
      setBrandKit(prev => ({
        ...prev,
        [activeFormat]: { ...prev[activeFormat], [field]: value }
      }));
    }
    setHasChanges(true);
  };
  
  // Handle symbol upload
  const handleSymbolUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload PNG or SVG only');
      return;
    }
    
    // For now, create a local URL (in production, upload to server)
    const url = URL.createObjectURL(file);
    handleBrandKitChange('symbol_url', url);
    toast.success('Symbol uploaded');
  };
  
  // Handle profile switch
  const handleProfileSwitch = (newProfileId) => {
    if (hasChanges) {
      if (!confirm('You have unsaved changes. Switch anyway?')) return;
    }
    selectProfile(newProfileId);
    navigate(`/app/admin/app-profile/${newProfileId}`, { replace: true });
  };
  
  // Save all changes atomically
  const handleSave = async () => {
    if (!activeProfile?.id) {
      toast.error('No profile selected');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        brand_kit: brandKit
      };
      
      const res = await fetch(`${API_URL}/api/admin/app-profiles/${activeProfile.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const result = await res.json();
        
        // Show storage-specific success message
        if (result.storage_mode === 'dropbox') {
          toast.success('Saved to Dropbox (persistent)');
        } else {
          toast.success(`Saved temporarily (expires in ${result.temp_ttl_hours || 72} hours). Connect Dropbox for permanent saving.`, {
            duration: 5000
          });
        }
        
        setStorageMode(result.storage_mode || 'temp');
        setHasChanges(false);
        refreshProfiles();
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to save - please try again');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save - network error');
    } finally {
      setSaving(false);
    }
  };
  
  // Connect platform - checks config first, then redirects
  const handleConnectPlatform = async (platform) => {
    if (!activeProfile?.id) {
      toast.error('Select an App Profile first');
      return;
    }
    
    // Check if platform is configured before redirecting
    // Backend returns: { platforms: { youtube: true, facebook: true, ... } }
    // Only show "not configured" if we EXPLICITLY got false from the backend
    const isConfigured = platformConfig?.platforms?.[platform];
    
    // If platformConfig hasn't loaded yet, allow the redirect (backend will return proper error)
    // If platformConfig loaded and platform is explicitly false, show error
    if (platformConfig?.platforms && isConfigured === false) {
      toast.error(`${platform.charAt(0).toUpperCase() + platform.slice(1)} is not configured. Please contact admin to add API credentials.`);
      return;
    }
    
    // Redirect to OAuth flow
    window.location.href = `${API_URL}/api/social/connect/${platform}?owner_type=app_profile&owner_id=${activeProfile.id}`;
  };
  
  // Save defaults (scoped by app_profile_id)
  const handleSaveDefaults = async () => {
    if (!activeProfile?.id) {
      toast.error('No profile selected');
      return;
    }
    setSavingDefaults(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/app-profiles/${activeProfile.id}/defaults`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_kit: brandKit })
      });
      
      if (res.ok) {
        const result = await res.json();
        
        // Show storage-specific success message
        if (result.storage_mode === 'dropbox') {
          toast.success('Defaults saved to Dropbox (persistent)');
        } else {
          toast.success(`Defaults saved temporarily (expires in ${storageTTL} hours). Connect Dropbox for permanent saving.`, {
            duration: 5000
          });
        }
        
        setDefaultsSaved(true);
        setStorageMode(result.storage_mode || 'temp');
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || 'Failed to save defaults - please try again');
      }
    } catch (error) {
      console.error('Save defaults error:', error);
      toast.error('Failed to save defaults - network error');
    } finally {
      setSavingDefaults(false);
    }
  };
  
  // Filter out legacy profiles
  const realProfiles = appProfiles.filter(p => p.id !== 'default-app-profile');
  
  // Current format config
  const currentConfig = brandKit[activeFormat];
  
  // Loading state
  if (loading && realProfiles.length === 0) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  
  // No profiles state
  if (realProfiles.length === 0) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="text-center py-12">
          <Briefcase className="h-16 w-16 text-[#8A93A6] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">No App Profiles</h2>
          <p className="text-[#8A93A6] mb-6">Create an App Profile to power the Self-Marketing Engine</p>
          <Button 
            onClick={() => navigate('/app/admin/app-profiles')}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create App Profile
          </Button>
        </div>
      </div>
    );
  }
  
  const connectedCount = platformStatus?.connected_count || 0;
  const connections = platformStatus?.connections || {};
  
  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="app-profile-page">
      {/* Profile Selector Bar */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/app/admin/app-profiles')}
          className="text-[#8A93A6] hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          All Profiles
        </Button>
        <div className="h-6 w-px bg-white/10" />
        <Label className="text-xs uppercase text-[#8A93A6]">App Profile</Label>
        <Select value={activeProfile?.id || ''} onValueChange={handleProfileSwitch}>
          <SelectTrigger className="bg-[#1C1F26] border-white/10 text-white w-64">
            <SelectValue placeholder="Select Profile" />
          </SelectTrigger>
          <SelectContent>
            {realProfiles.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => navigate('/app/admin/app-profiles')}
          className="border-white/10"
        >
          Manage Profiles
        </Button>
      </div>
      
      {/* ========== SECTION 1: HEADER ========== */}
      <div className="flex items-center gap-6 mb-8 p-6 rounded-xl bg-[#1C1F26] border border-white/10">
        {/* LEFT: Avatar/Symbol (display-only, reflects Brand Kit symbol) */}
        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center flex-shrink-0">
          {brandKit.symbol_url ? (
            <img 
              src={brandKit.symbol_url} 
              alt="Symbol" 
              className="w-full h-full object-contain rounded-xl"
              data-testid="header-avatar"
            />
          ) : (
            <Briefcase className="h-10 w-10 text-[#8A93A6]" />
          )}
        </div>
        
        {/* CENTER: Editable Name & Description */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder="App Profile Name"
              className="text-2xl font-bold bg-transparent border-dashed border-white/20 text-white h-auto py-1 px-2 focus:border-cyan-500"
              data-testid="header-name-input"
            />
            <Pencil className="h-4 w-4 text-[#8A93A6] flex-shrink-0" />
          </div>
          <div className="flex items-start gap-2">
            <Textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Marketing-facing description (click to edit)"
              className="bg-transparent border-dashed border-white/20 text-[#8A93A6] resize-none py-1 px-2 focus:border-cyan-500"
              rows={2}
              data-testid="header-description-input"
            />
            <Pencil className="h-4 w-4 text-[#8A93A6] flex-shrink-0 mt-2" />
          </div>
        </div>
        
        {/* RIGHT: SME Badge (read-only) */}
        <Badge variant="outline" className="border-pink-500/30 text-pink-400 flex-shrink-0 px-3 py-1">
          <Zap className="h-3 w-3 mr-1" />
          Self-Marketing Engine
        </Badge>
      </div>
      
      {/* ========== SECTION 2 & 3: BRAND KIT & PREVIEW ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* LEFT: Marketing Brand Kit */}
        <Card className="glass h-full">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Palette className="h-5 w-5 text-pink-500" />
              Marketing Brand Kit
            </CardTitle>
            <CardDescription>Controls SME visuals only</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Format Toggle (REQUIRED) */}
            <div className="flex items-center justify-center p-1 bg-black/40 rounded-lg">
              <button
                onClick={() => setActiveFormat('sf')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeFormat === 'sf' 
                    ? 'bg-pink-500 text-white' 
                    : 'text-[#8A93A6] hover:text-white'
                }`}
                data-testid="format-toggle-sf"
              >
                Short Form
              </button>
              <button
                onClick={() => setActiveFormat('lf')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  activeFormat === 'lf' 
                    ? 'bg-pink-500 text-white' 
                    : 'text-[#8A93A6] hover:text-white'
                }`}
                data-testid="format-toggle-lf"
              >
                Long Form
              </button>
            </div>
            
            {/* 1️⃣ Symbol */}
            <div className="space-y-3">
              <Label className="text-white font-medium">Symbol</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
                  {brandKit.symbol_url ? (
                    <img src={brandKit.symbol_url} alt="Symbol" className="w-full h-full object-contain" />
                  ) : (
                    <Image className="h-6 w-6 text-[#8A93A6]" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fileInputRef.current?.click()}
                    className="border-white/10 w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {brandKit.symbol_url ? 'Replace Symbol' : 'Upload Symbol'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.svg"
                    onChange={handleSymbolUpload}
                    className="hidden"
                  />
                </div>
              </div>
              {/* Position (per format) */}
              <div>
                <Label className="text-xs text-[#8A93A6]">Position ({activeFormat === 'sf' ? 'Short Form' : 'Long Form'})</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {[
                    { value: 'tl', label: 'TL' },
                    { value: 'tr', label: 'TR' },
                    { value: 'bl', label: 'BL' },
                    { value: 'br', label: 'BR' }
                  ].map((pos) => (
                    <button
                      key={pos.value}
                      onClick={() => handleBrandKitChange('symbol_position', pos.value)}
                      className={`py-2 rounded text-xs font-medium transition-all ${
                        currentConfig.symbol_position === pos.value
                          ? 'bg-pink-500 text-white'
                          : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* 2️⃣ Captions */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium">Captions</Label>
                <Switch
                  checked={currentConfig.captions_enabled}
                  onCheckedChange={(v) => handleBrandKitChange('captions_enabled', v)}
                  data-testid="captions-toggle"
                />
              </div>
              
              {currentConfig.captions_enabled && (
                <div className="space-y-3 pl-4 border-l-2 border-pink-500/30">
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Font</Label>
                    <Select 
                      value={currentConfig.caption_font} 
                      onValueChange={(v) => handleBrandKitChange('caption_font', v)}
                    >
                      <SelectTrigger className="bg-black/40 border-white/10 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#8A93A6]">Primary Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="color"
                          value={currentConfig.caption_primary_color}
                          onChange={(e) => handleBrandKitChange('caption_primary_color', e.target.value)}
                          className="h-8 w-12 p-1 bg-transparent border-white/10"
                        />
                        <Input
                          value={currentConfig.caption_primary_color}
                          onChange={(e) => handleBrandKitChange('caption_primary_color', e.target.value)}
                          className="bg-black/40 border-white/10 text-white text-xs flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#8A93A6]">Secondary Color</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="color"
                          value={currentConfig.caption_secondary_color}
                          onChange={(e) => handleBrandKitChange('caption_secondary_color', e.target.value)}
                          className="h-8 w-12 p-1 bg-transparent border-white/10"
                        />
                        <Input
                          value={currentConfig.caption_secondary_color}
                          onChange={(e) => handleBrandKitChange('caption_secondary_color', e.target.value)}
                          className="bg-black/40 border-white/10 text-white text-xs flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Position</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                      {['top', 'center', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => handleBrandKitChange('caption_position', pos)}
                          className={`py-2 rounded text-xs font-medium capitalize transition-all ${
                            currentConfig.caption_position === pos
                              ? 'bg-pink-500 text-white'
                              : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                          }`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* 3️⃣ Progress Bar */}
            <div className="space-y-3 pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium">Progress Bar</Label>
                <Switch
                  checked={currentConfig.progress_bar_enabled}
                  onCheckedChange={(v) => handleBrandKitChange('progress_bar_enabled', v)}
                  data-testid="progress-bar-toggle"
                />
              </div>
              
              {currentConfig.progress_bar_enabled && (
                <div className="pl-4 border-l-2 border-pink-500/30 space-y-3">
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="color"
                        value={currentConfig.progress_bar_color}
                        onChange={(e) => handleBrandKitChange('progress_bar_color', e.target.value)}
                        className="h-8 w-12 p-1 bg-transparent border-white/10"
                      />
                      <Input
                        value={currentConfig.progress_bar_color}
                        onChange={(e) => handleBrandKitChange('progress_bar_color', e.target.value)}
                        className="bg-black/40 border-white/10 text-white text-xs flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8A93A6]">Position</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {['top', 'bottom'].map((pos) => (
                        <button
                          key={pos}
                          onClick={() => handleBrandKitChange('progress_bar_position', pos)}
                          className={`py-2 rounded text-xs font-medium capitalize transition-all ${
                            currentConfig.progress_bar_position === pos
                              ? 'bg-pink-500 text-white'
                              : 'bg-black/40 text-[#8A93A6] hover:text-white border border-white/10'
                          }`}
                          data-testid={`progress-bar-position-${pos}`}
                        >
                          {pos}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* RIGHT: Live Marketing Preview */}
        <Card className="glass h-full">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Eye className="h-5 w-5 text-green-500" />
              Live Marketing Preview
            </CardTitle>
            <CardDescription>
              {activeFormat === 'sf' ? 'Short-Form Preview' : 'Long-Form Preview'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Preview Container - Fixed size, internal aspect ratio changes */}
            <div 
              className="relative rounded-lg bg-black border border-white/10 overflow-hidden h-[400px] flex items-center justify-center"
              data-testid="live-preview"
            >
              {/* Inner preview canvas with correct aspect ratio */}
              <div 
                className={`relative bg-gradient-to-br from-gray-800 to-gray-900 ${
                  activeFormat === 'sf' 
                    ? 'h-full aspect-[9/16]' 
                    : 'w-full aspect-video'
                }`}
                style={{ maxHeight: '100%', maxWidth: '100%' }}
              >
                {/* Format Label */}
                <div className="absolute top-2 left-2 z-10">
                  <Badge className="bg-black/60 text-white text-xs">
                    {activeFormat === 'sf' ? 'Short-Form Preview' : 'Long-Form Preview'}
                  </Badge>
                </div>
                
                {/* Symbol (position-based) */}
                {brandKit.symbol_url && (
                  <div 
                    className={`absolute w-10 h-10 p-1 ${
                      currentConfig.symbol_position === 'tl' ? 'top-2 left-2' :
                      currentConfig.symbol_position === 'tr' ? 'top-2 right-2' :
                      currentConfig.symbol_position === 'bl' ? 'bottom-2 left-2' :
                      'bottom-2 right-2'
                    }`}
                  >
                    <img 
                      src={brandKit.symbol_url} 
                      alt="Symbol" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                
                {/* Progress Bar - Position-aware */}
                {currentConfig.progress_bar_enabled && (
                  <div 
                    className={`absolute left-0 right-0 h-1 ${
                      currentConfig.progress_bar_position === 'bottom' ? 'bottom-0' : 'top-0'
                    }`}
                  >
                    <div 
                      className="h-full w-2/3"
                      style={{ backgroundColor: currentConfig.progress_bar_color }}
                    />
                  </div>
                )}
                
                {/* Caption Preview */}
                {currentConfig.captions_enabled && (
                  <div 
                    className={`absolute left-0 right-0 px-3 text-center ${
                      currentConfig.caption_position === 'top' ? 'top-10' :
                      currentConfig.caption_position === 'center' ? 'top-1/2 -translate-y-1/2' :
                      'bottom-10'
                    }`}
                  >
                    <p 
                      className="text-sm font-semibold drop-shadow-lg"
                      style={{ 
                        fontFamily: currentConfig.caption_font,
                        color: currentConfig.caption_primary_color
                      }}
                    >
                      Sample <span style={{ color: currentConfig.caption_secondary_color }}>Caption</span> Text
                    </p>
                  </div>
                )}
                
                {/* Center content placeholder */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-white/10 mx-auto mb-2 flex items-center justify-center">
                      <Eye className="h-6 w-6 text-white/30" />
                    </div>
                    <p className="text-white/40 text-xs">Content Preview</p>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-[#8A93A6] mt-4 text-center">
              Preview updates automatically as you modify Brand Kit settings
            </p>
            
            {/* Defaults (inside same card) */}
            <div className="pt-4 border-t border-white/10 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Bookmark className="h-4 w-4 text-cyan-500" />
                <span className="text-white font-medium text-sm">Defaults</span>
                {storageMode === 'temp' && (
                  <Badge variant="outline" className="ml-auto border-yellow-500/30 text-yellow-400 text-xs">
                    Temp ({storageTTL}h)
                  </Badge>
                )}
                {storageMode === 'dropbox' && (
                  <Badge variant="outline" className="ml-auto border-green-500/30 text-green-400 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Persistent
                  </Badge>
                )}
              </div>
              <div className="p-3 rounded-lg bg-black/40 border border-white/10 mb-3">
                <p className="text-xs text-[#8A93A6]">
                  <strong className="text-white">Current:</strong> {defaultsSaved || activeProfile?.has_defaults ? 'Custom defaults saved' : 'Using system defaults'}
                </p>
                <p className="text-xs text-[#8A93A6] mt-1">
                  {storageMode === 'dropbox' 
                    ? 'Defaults are permanently stored in Dropbox.' 
                    : `Defaults expire in ${storageTTL} hours. Connect Dropbox for permanent saving.`}
                </p>
              </div>
              <Button 
                onClick={handleSaveDefaults}
                disabled={savingDefaults}
                size="sm"
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-defaults-btn"
              >
                <Bookmark className="h-3 w-3 mr-2" />
                {savingDefaults ? 'Saving...' : 'Set current design as default'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* ========== SECTION 4: STORAGE & PLATFORMS ========== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Storage */}
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="h-5 w-5 text-purple-500" />
              App Marketing Storage
              {dropboxStatus?.internal_dropbox_connected && (
                <Badge variant="outline" className="ml-2 border-green-500/30 text-green-400 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </CardTitle>
            <CardDescription>App-level Dropbox for marketing assets</CardDescription>
          </CardHeader>
          <CardContent>
            {dropboxStatus?.internal_dropbox_connected ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-white text-sm font-medium">Internal Dropbox Connected</p>
                  <p className="text-xs text-[#8A93A6] font-mono mt-1">{dropboxStatus.account_email || 'N/A'}</p>
                  <p className="text-xs text-[#8A93A6] font-mono">{dropboxStatus.root_folder_path || '/consistency/Internal'}/</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={handleDisconnectDropbox}
                    disabled={disconnectingDropbox}
                    data-testid="disconnect-dropbox-btn"
                  >
                    {disconnectingDropbox ? 'Disconnecting...' : 'Disconnect'}
                  </Button>
                  <Button variant="outline" className="flex-1 border-white/10" onClick={() => navigate('/settings/storage')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Storage Settings
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                  <Database className="h-6 w-6 text-yellow-500 mx-auto mb-1" />
                  <p className="text-yellow-400 text-xs">No storage connected</p>
                  <p className="text-xs text-[#8A93A6] mt-1">Connect Dropbox for persistent file storage</p>
                </div>
                <Button 
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  onClick={handleConnectDropbox}
                  data-testid="connect-dropbox-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Connect Dropbox Storage
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* RIGHT: Platforms */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">Connected Platforms</CardTitle>
              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                {connectedCount} connected
              </Badge>
            </div>
            <CardDescription>Where SME publishes content</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <PlatformRow
              platform="youtube" name="YouTube" icon={Youtube}
              color="text-red-500" bgColor="bg-red-500/10" borderColor="border-red-500/30"
              connection={connections.youtube}
              isConfigured={platformConfig?.platforms?.youtube}
              onConnect={() => handleConnectPlatform('youtube')}
              onManage={() => navigate('/social-accounts')}
            />
            <PlatformRow
              platform="tiktok" name="TikTok" icon={Music2}
              color="text-pink-500" bgColor="bg-pink-500/10" borderColor="border-pink-500/30"
              connection={connections.tiktok}
              isConfigured={platformConfig?.platforms?.tiktok}
              onConnect={() => handleConnectPlatform('tiktok')}
              onManage={() => navigate('/social-accounts')}
            />
            <PlatformRow
              platform="instagram" name="Instagram" icon={Instagram}
              color="text-purple-500" bgColor="bg-purple-500/10" borderColor="border-purple-500/30"
              connection={connections.instagram}
              isConfigured={platformConfig?.platforms?.instagram}
              onConnect={() => handleConnectPlatform('instagram')}
              onManage={() => navigate('/social-accounts')}
            />
            <PlatformRow
              platform="facebook" name="Facebook" icon={Facebook}
              color="text-blue-500" bgColor="bg-blue-500/10" borderColor="border-blue-500/30"
              connection={connections.facebook}
              isConfigured={platformConfig?.platforms?.facebook}
              onConnect={() => handleConnectPlatform('facebook')}
              onManage={() => navigate('/social-accounts')}
            />
          </CardContent>
        </Card>
      </div>
      
      {/* ========== SAVE BUTTON ========== */}
      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`px-8 ${hasChanges ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-gray-600 cursor-not-allowed'}`}
          data-testid="save-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

/** Platform Row - With config status */
function PlatformRow({ platform, name, icon: Icon, color, bgColor, borderColor, connection, isConfigured = true, onConnect, onManage }) {
  const isConnected = connection?.is_connected;
  
  return (
    <div className={`flex items-center justify-between p-2.5 rounded-lg border ${isConnected ? borderColor : 'border-gray-700'} ${isConnected ? bgColor : 'bg-gray-800/50'}`}>
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded ${bgColor}`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <span className="text-white text-sm font-medium">{name}</span>
        {isConnected && (
          <Badge className="bg-green-500/20 text-green-400 text-xs py-0">
            <CheckCircle className="w-3 h-3 mr-1" />
            Connected
          </Badge>
        )}
        {!isConnected && !isConfigured && (
          <span className="text-xs text-yellow-500">Not configured</span>
        )}
      </div>
      {isConnected ? (
        <Button variant="outline" size="sm" onClick={onManage} className={`${borderColor} ${color} text-xs h-7`}>
          Manage
        </Button>
      ) : (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onConnect} 
          disabled={!isConfigured}
          className={isConfigured ? `${borderColor} ${color} text-xs h-7` : 'border-gray-600 text-gray-500 text-xs h-7 cursor-not-allowed'}
        >
          <ExternalLink className="w-3 h-3 mr-1" />
          Connect
        </Button>
      )}
    </div>
  );
}
